'use client';

import DownloadIcon from 'assets/icons/download.svg';
import IconButton from 'components/controls/buttons/icon-button';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import styles from './download-menu-button.module.scss';

const VIEWPORT_PADDING_PX = 16;

export interface DownloadMenuItem {
	href: string;
	id: string;
	label: string;
	meta?: string;
	onClick?: () => void;
}

interface Props {
	buttonClassName?: string;
	buttonLabel: string;
	iconOnly?: boolean;
	items: DownloadMenuItem[];
	lightMode?: boolean;
	menuAlign?: 'left' | 'right';
	menuDirection?: 'up' | 'down';
	small?: boolean;
	wrapperClassName?: string;
}

const DownloadMenuButton = ({
	buttonClassName,
	buttonLabel,
	iconOnly = false,
	items,
	lightMode,
	menuAlign = 'right',
	menuDirection = 'down',
	small,
	wrapperClassName,
}: Props) => {
	const menuId = useId();
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		const menu = menuRef.current;
		if (!open || !menu) {
			return;
		}

		// Reset before measuring so we don't compound prior shifts.
		menu.style.transform = '';
		const rect = menu.getBoundingClientRect();

		let shiftX = 0;
		if (rect.left < VIEWPORT_PADDING_PX) {
			shiftX = VIEWPORT_PADDING_PX - rect.left;
		} else if (rect.right > window.innerWidth - VIEWPORT_PADDING_PX) {
			shiftX = window.innerWidth - VIEWPORT_PADDING_PX - rect.right;
		}

		if (shiftX !== 0) {
			menu.style.transform = `translateX(${shiftX}px)`;
		}
	}, [open]);

	useEffect(() => {
		const handlePointerDown = (event: globalThis.MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(event.target as Node)
			) {
				setOpen(false);
			}
		};

		const handleEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handlePointerDown);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handlePointerDown);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	if (items.length === 0) {
		return null;
	}

	const toggleMenu = () => setOpen((currentState) => !currentState);

	const handleItemClick = (item: DownloadMenuItem) => {
		item.onClick?.();
		setOpen(false);
	};

	return (
		<div
			className={`${styles.wrapper} ${wrapperClassName ?? ''}`.trim()}
			ref={wrapperRef}
		>
			<IconButton
				aria-controls={menuId}
				aria-expanded={open}
				aria-haspopup="menu"
				className={buttonClassName}
				icon={
					iconOnly ? (
						<span className={styles.iconOnlyTrigger}>
							<DownloadIcon />
						</span>
					) : (
						<DownloadIcon />
					)
				}
				jsxLabel={
					iconOnly ? undefined : (
						<span className={styles.triggerLabel}>
							<span>{buttonLabel}</span>
						</span>
					)
				}
				label={buttonLabel}
				lightMode={lightMode}
				onClick={toggleMenu}
				showLabel={!iconOnly}
				small={small}
			/>
			{open && (
				<div
					className={`${styles.menu} ${menuAlign === 'left' ? styles.menuAlignLeft : styles.menuAlignRight} ${menuDirection === 'up' ? styles.menuDirectionUp : styles.menuDirectionDown}`}
					id={menuId}
					ref={menuRef}
					role="menu"
				>
					{items.map((item) => (
						<a
							className={styles.menuItem}
							href={item.href}
							key={item.id}
							onClick={() => handleItemClick(item)}
							rel="noreferrer"
							role="menuitem"
							target="_blank"
						>
							<span className={styles.menuItemLabel}>{item.label}</span>
							{item.meta && (
								<span className={styles.menuItemMeta}>{item.meta}</span>
							)}
						</a>
					))}
				</div>
			)}
		</div>
	);
};

export default DownloadMenuButton;
